import { useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { CRUISE_SEARCH_DEPARTURE_REGIONS } from 'constants/config/region'
import { OFFER_TYPE_CRUISE } from 'constants/offer'
import Places from 'constants/places'
import useOfferList from 'hooks/Offers/useOfferList'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import LoyaltySpendPointsCarouselSection from './LoyaltySpendPointsCarouselSection'
import LoyaltySpendPointsOfferFilterPrefetch from './LoyaltySpendPointsOfferFilterPrefetch'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { MAX_CAROUSEL_FILTER_OPTIONS, MINIMUM_OFFERS_TO_SHOW_CAROUSEL, SPEND_POINTS_CAROUSEL_OFFER_LIMIT } from './loyaltySpendPointsConstants'
import useLoyaltySpendPointsFilterAvailability from './useLoyaltySpendPointsFilterAvailability'
import useLoyaltySpendPointsOfferListBurnSort from './useLoyaltySpendPointsOfferListBurnSort'

const ALL_DEPARTURE_REGIONS_VALUE = ''
const SORT_BY_OPTION: Record<SpendPointsSortOption, App.OfferListSortByOptions | undefined> = {
  recommended: undefined,
  // Cash sort paginates the search candidate pool; burn sort re-orders on the client.
  'lowest-points': 'price.asc',
  'highest-points': 'price.desc',
}

interface Props {
  title: ReactNode
  sortOption: SpendPointsSortOption
}

function LoyaltySpendPointsCruiseCarousel(props: Props) {
  const { title, sortOption } = props
  const intl = useIntl()
  const { showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const [selectedDepartureRegion, setSelectedDepartureRegion] = useState(ALL_DEPARTURE_REGIONS_VALUE)
  const [cashPriceCap, setCashPriceCap] = useState<number>()

  const departureRegionOptions = useMemo(() => [
    {
      value: ALL_DEPARTURE_REGIONS_VALUE,
      label: intl.formatMessage({
        id: 'xgsbMHpJ0hZoqyyDo8fb',
        defaultMessage: 'Around the world',
        description: 'Destination label for anywhere/worldwide cruise search',
      }),
    },
    ...CRUISE_SEARCH_DEPARTURE_REGIONS
      .slice(0, MAX_CAROUSEL_FILTER_OPTIONS - 1)
      .map(region => ({
        value: region.placeIds,
        label: region.name,
      })),
  ], [intl])

  const onDepartureRegionChange = useCallback((departureRegion: string) => {
    setSelectedDepartureRegion(currentDepartureRegion => (
      currentDepartureRegion === departureRegion ? ALL_DEPARTURE_REGIONS_VALUE : departureRegion
    ))
  }, [])

  useEffect(() => {
    setCashPriceCap(undefined)
  }, [showOffersWithinBalance])

  const sortBy = SORT_BY_OPTION[sortOption]
  const offerListFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_CRUISE],
    destinationId: Places.Anywhere.id,
    departureIds: selectedDepartureRegion ? [selectedDepartureRegion] : undefined,
    limit: SPEND_POINTS_CAROUSEL_OFFER_LIMIT,
    sortBy,
    viewPriceInPoints: true,
    maxPrice: showOffersWithinBalance && cashPriceCap !== undefined
      ? cashPriceCap
      : undefined,
  }), [cashPriceCap, selectedDepartureRegion, showOffersWithinBalance, sortBy])
  const rawOfferList = useOfferList(offerListFilters)
  const offerList = useMemo(() => ({
    ...rawOfferList,
    offerIds: rawOfferList.offerIds.filter(id => !id.startsWith('tour')),
  }), [rawOfferList])
  const getBurnRequest = useCallback((offer: App.AnyOffer) => {
    if (!isCruiseOffer(offer)) return undefined

    const price = offer.mainDepartureDetails.lowestOverallPriceDetails?.total
    const loyaltyProductType = getLoyaltyProductType(offer)
    return price && loyaltyProductType ? { standard: { price, loyaltyProductType } } : undefined
  }, [])
  const sortedOfferList = useLoyaltySpendPointsOfferListBurnSort({
    offerList,
    sortOption,
    getBurnRequest,
  })

  useEffect(() => {
    if (sortedOfferList.cashPriceCap !== undefined) {
      setCashPriceCap(currentCap => Math.max(currentCap ?? 0, sortedOfferList.cashPriceCap!))
    }
  }, [sortedOfferList.cashPriceCap])

  const { visibleOptions: visibleDepartureRegionOptions, onAvailabilityChange: onDepartureRegionAvailabilityChange, isResolved: areDepartureRegionFiltersResolved, shouldShowFilters } = useLoyaltySpendPointsFilterAvailability({
    options: departureRegionOptions,
    selectedValue: selectedDepartureRegion,
    defaultValue: ALL_DEPARTURE_REGIONS_VALUE,
    enabled: showOffersWithinBalance,
    availabilityKey: cashPriceCap?.toString(),
    onSelectedValueChange: setSelectedDepartureRegion,
  })

  const departureRegionPrefetchFilters = useMemo(() => (
    departureRegionOptions
      .filter(option => !!option.value)
      .map(option => ({
        value: option.value,
        filters: {
          ...offerListFilters,
          departureIds: [option.value],
          limit: MINIMUM_OFFERS_TO_SHOW_CAROUSEL,
        },
      }))
  ), [departureRegionOptions, offerListFilters])

  if (showOffersWithinBalance && !selectedDepartureRegion && !sortedOfferList.offerList.fetching && sortedOfferList.offerList.offerIds.length < MINIMUM_OFFERS_TO_SHOW_CAROUSEL) {
    return null
  }

  return <>
    {showOffersWithinBalance && cashPriceCap !== undefined && departureRegionPrefetchFilters.map(({ value, filters }) => <LoyaltySpendPointsOfferFilterPrefetch
      key={value}
      value={value}
      filters={filters}
      onAvailabilityChange={onDepartureRegionAvailabilityChange}
    />)}
    <LoyaltySpendPointsCarouselSection
      title={title}
      viewAllTo="/cruises"
      filters={offerListFilters}
      offerList={sortedOfferList.offerList}
      filterOptions={visibleDepartureRegionOptions}
      selectedFilter={selectedDepartureRegion}
      onFilterChange={onDepartureRegionChange}
      showFilters={shouldShowFilters}
      filtersLoading={showOffersWithinBalance && shouldShowFilters && !areDepartureRegionFiltersResolved}
      testId="loyalty-spend-points-cruises-carousel"
    />
  </>
}

export default LoyaltySpendPointsCruiseCarousel
